<template>
  <b-container>
    <b-row>
      <b-col cols="12">
        <b-button variant="warning" size="sm"
          v-if="this.newDataAvailable === true"
          class="btn-reload-demands-and-purchase-offers"
          @click="reloadData()"
        >
          New data available. Click here to reload.
          <b-icon-arrow-repeat/>
        </b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="12" sm="7"><h1>Accepted Purchase Offers</h1></b-col>
      <b-col cols="12" sm="5" v-if="status === localConstants.STATUS_FINISHED_LOADING" class="text-right">
        Last update: {{ lastUpdate }}
      </b-col>
    </b-row>
    <div v-if="status === localConstants.STATUS_LOADING">
      <b-row>
        <b-col cols="12" class="text-center col-loading">
          <h3>Loading purchase offers <b-spinner/></h3>
        </b-col>
      </b-row>
    </div>
    <div v-if="status === localConstants.STATUS_ERROR_LOADING">
      <b-row class="justify-content-center">
        <b-col cols="8" class="text-center col-loading">
          <div class="alert alert-danger">
            Temporary Error during loading. Please try to refresh the page and see if the error persists.
          </div>
        </b-col>
      </b-row>
    </div>
    <transition name="fade">
      <div v-if="status === localConstants.STATUS_FINISHED_LOADING">
        <b-row>
          <b-col cols="12" md="3">
            <label for="textFilter">Text</label>
            <input
              type="text"
              v-model="textFilter"
              id="textFilter"
              class="form-control form-control-sm"
              placeholder="Search in Merchant Email, Variant, Demand Id"
            />
          </b-col>

          <b-col cols="12" sm="3" class="purchase-offer-date-filter">
            <b-row>
              <b-col cols="12" sm="12"><strong>Purchase Offer Dates</strong></b-col>
            </b-row>
            <b-row class="purchase-offer-date-filter-row">
              <b-col cols="6" sm="6">
                <label for="purchase-offer-from-date">From</label>
                <input
                  type="text"
                  class="form-control form-control-sm purchase-offer-date"
                  id="purchase-offer-from-date"
                  v-model="purchaseOfferFromDate"
                />
              </b-col>
              <b-col cols="6" sm="6">
                <label for="purchase-offer-to-date">To</label>
                <input
                  type="text"
                  class="form-control form-control-sm purchase-offer-date"
                  id="purchase-offer-to-date"
                  v-model="purchaseOfferToDate"
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12" sm="12">
                <div class="btn btn-sm btn-primary btn-block" @click="applyPurchaseOfferDateFilter()">
                  Apply Date Filter
                </div>
              </b-col>
            </b-row>
          </b-col>

          <b-col cols="12" md="2">
            <br />
            <b-button variant="secondary" size="sm" block
              @click="resetFilter()"
              :disabled="!filterActive"
            >
              Reset Filter <b-icon-arrow-counterclockwise/>
            </b-button>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="4" md="4">
            Page Size
            <ul class="pagination page-size-selection">
              <li
                v-for="pageSizeSelection in [10, 20, 50]"
                class="page-item"
                :class="{ active: pageSizeSelection === pageSize }"
                @click="pageSize = pageSizeSelection"
                :key="pageSizeSelection"
              >
                <div class="page-link">{{ pageSizeSelection }}</div>
              </li>
            </ul>
          </b-col>
          <b-col cols="12" md="8" class="text-right">
            Showing {{ firstResultInView }} - {{ lastResultInView }} of
            <strong>{{ numberOfFilteredPurchaseOffers }}</strong> / Total
            {{ numberOfTotalPurchaseOffers }}
          </b-col>
        </b-row>

        <hr />

        <b-row class="row-header d-none d-sm-flex" @click.stop>
          <b-col cols="12" md="3" class="col-content">
            Merchant Email / Message
          </b-col>
          <b-col cols="12" md="3" class="col-content">
            <b-row>
              <b-col cols="12">
                <b-row><b-col>demandId</b-col></b-row>
                <b-row><b-col>variantKey</b-col></b-row>
                <b-row>
                  <b-col>acceptedPrice acceptedCurrency</b-col>
                </b-row>
                <b-row><b-col>acceptedAt</b-col></b-row>
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="12" md="2" class="col-content">Accessible by</b-col>
          <b-col cols="12" md="2" class="col-content">Offered</b-col>
          <b-col cols="12" md="2" class="col-content">Action</b-col>
        </b-row>

        <b-row v-for="purchaseOffer in filteredPaginatedPurchaseOffers" :key="purchaseOffer.demandId">
          <admin-accepted-purchase-offer-row
            :purchase-offer="purchaseOffer"
            @purchase-offer-failed="reloadData()"
          ></admin-accepted-purchase-offer-row>
        </b-row>

        <hr />
        <pagination
          :items="filteredPurchaseOffers"
          :currentPage="currentPage"
          :pageSize="pageSize"
          @changePage="changePage"
        ></pagination>
      </div>
    </transition>
  </b-container>
</template>

<script>
import store from 'src/store/index';
import AdminAcceptedPurchaseOfferRow from 'Components/admin/demand/AdminAcceptedPurchaseOfferRow';
import Pagination from 'Components/common/Pagination';
import { format, addWeeks } from 'date-fns';
import Pikaday from 'pikaday';
import { timeFormatMixin } from 'Mixins/timeFormatMixin';

export default {
  name: 'AdminAcceptedPurchaseOffers',
  components: {
    Pagination,
    AdminAcceptedPurchaseOfferRow,
  },
  mixins: [timeFormatMixin],
  data: () => {
    return {
      newDataAvailable: false,
      lastPurchaseOfferUpdate: null,
      currentPage: 1,
      pageSize: 20,
      status: '',
      textFilter: '',
      purchaseOffers: [],
      purchaseOfferFromDate: null,
      purchaseOfferToDate: null,
      updateInterval: null,
      localConstants: {
        STATUS_LOADING: 'loading',
        STATUS_ERROR_LOADING: 'errorLoading',
        STATUS_FINISHED_LOADING: 'finishedLoading',
      },
    };
  },
  computed: {
    filterActive() {
      return this.textFilter !== '';
    },
    numberOfTotalPurchaseOffers() {
      return this.purchaseOffers.length;
    },
    numberOfFilteredPages() {
      return Math.ceil(this.filteredPurchaseOffers.length / this.pageSize);
    },
    numberOfFilteredPurchaseOffers() {
      return this.filteredPurchaseOffers.length;
    },
    firstResultInView() {
      return Math.min(this.filteredPurchaseOffers.length, (this.currentPage - 1) * this.pageSize + 1);
    },
    lastResultInView() {
      if (this.currentPage * this.pageSize > this.filteredPurchaseOffers.length) {
        return this.filteredPurchaseOffers.length;
      }
      return this.currentPage * this.pageSize;
    },
    filteredPaginatedPurchaseOffers() {
      return this.filteredPurchaseOffers.slice(
        (this.currentPage - 1) * this.pageSize,
        this.currentPage * this.pageSize,
      );
    },
    filteredPurchaseOffers() {
      return this.purchaseOffers.filter((purchaseOffer) => {
        let purchaseOfferVariantKey = purchaseOffer.variantKey.toLowerCase();
        let purchaseOfferDemandId = purchaseOffer.demandId.toLowerCase();
        let purchaseOfferMerchantEmail = purchaseOffer.userEmail.toLowerCase();

        let textFilter = this.textFilter.toLowerCase().trim();
        return !(
          purchaseOfferVariantKey.indexOf(textFilter) === -1 &&
          purchaseOfferDemandId.indexOf(textFilter) === -1 &&
          purchaseOfferMerchantEmail.indexOf(textFilter) === -1
        );
      });
    },
    lastUpdate() {
      return this.formattedTime(this.lastPurchaseOfferUpdate);
    },
  },
  methods: {
    applyPurchaseOfferDateFilter() {
      this.reloadData();
    },
    changePage(page) {
      this.currentPage = page;
    },
    resetFilter() {
      this.textFilter = '';
    },
    loadAcceptedPurchaseOffers(startDate, endDate) {
      return new Promise((resolve, reject) => {
        store
          .dispatch('loadAdminAcceptedPurchaseOffers', { startDate, endDate })
          .then((response) => {
            this.purchaseOffers = response.data.purchaseOffers;

            this.lastPurchaseOfferUpdate = response.data.lastUpdate;
            resolve();
          })
          .catch(() => {
            reject();
          });
      });
    },
    reloadData() {
      return new Promise((resolve, reject) => {
        this.status = this.localConstants.STATUS_LOADING;

        this.loadAcceptedPurchaseOffers(this.purchaseOfferFromDate, this.purchaseOfferToDate)
          .then(() => {
            this.status = this.localConstants.STATUS_FINISHED_LOADING;

            let self = this;

            this.$nextTick(() => {
              new Pikaday({
                field: document.getElementById('purchase-offer-from-date'),
                format: 'yyyy-MM-dd',
                toString(date) {
                  return self.formattedDate(date);
                },
                parse(dateString) {
                  return self.parseDate(dateString);
                },
                onSelect: function() {
                  self.purchaseOfferFromDate = this.toString();
                },
              });

              new Pikaday({
                field: document.getElementById('purchase-offer-to-date'),
                format: 'yyyy-MM-dd',
                toString(date) {
                  return self.formattedDate(date);
                },
                parse(dateString) {
                  return self.parseDate(dateString);
                },
                onSelect: function() {
                  self.purchaseOfferToDate = this.toString();
                },
              });
            });
            resolve();
          })
          .catch(() => {
            this.status = this.localConstants.STATUS_ERROR_LOADING;
            reject();
          });
      });
    },
  },
  mounted() {
    this.purchaseOfferFromDate = format(addWeeks(new Date(), -2), 'yyyy-MM-dd');
    this.purchaseOfferToDate = format(new Date(), 'yyyy-MM-dd');

    this.reloadData();
  },
  beforeDestroy() {
    clearInterval(this.updateInterval);
  },
};
</script>

<style lang="scss" scoped>
.purchase-offer-date-filter {
  padding-bottom: 10px;
}

.purchase-offer-date-filter-row {
  margin-bottom: 1em;
}

.page-size-selection .page-link {
  font-size: 0.85rem;
  padding: 0.35rem 0.5rem;
}

.row-header {
  padding-top: 3px;
  padding-bottom: 5px;
  border: 1px solid #fff;
  background-color: #777;
  color: #fff;
}
</style>
